/*!
 * Copyright (c) Microsoft Corporation. All rights reserved.
 */

import React from "react";

import { useDispatch } from "react-redux";
import { useHistory } from "react-router";

import Pagination from "rc-pagination";

import {
  DetailsList,
  DetailsListLayoutMode,
  Dropdown,
  IColumn,
  IDetailsRowProps,
  Link,
  SelectionMode,
  Spinner,
  SpinnerSize,
} from "@fluentui/react";

import * as VisualStudioBenefitActions from "Dataflows/VisualStudioBenefit/VisualStudioBenefitAction";

import ChevronIcon from "Components/Common/ChevronIcon/ChevronIcon";
import { PaginationRow } from "Components/Common/Pagination/Pagination.styles";
import { detailListStyles, spinnerStyles } from "Components/Common/SharedListStyles.styles";
import { getAzureBenefitRequestDetails } from "Dataflows/AzureBenefitRequests/AzureBenefitRequestsAction";
import { IAzureBenefitRequest } from "Interfaces/AzureBenefitRequests/IAzureBenefitRequest";
import { formatDate } from "Utility/FormatHelper";

import { IAzureRequestsListProps } from "./IAzureRequestsListProps";

const AzureRequestsList = (props: IAzureRequestsListProps) => {
  const dispatch = useDispatch();
  const history = useHistory();

  const {
    data,
    loadAzureRequestsError,
    isLoadingAzureRequests,
    filteredCount,
    currentPage,
    pageSize,
    tenant,
  } = props;

  const idLink = (id: number): React.ReactElement => {
    return <Link>{id}</Link>;
  };

  const formatExpeditedField = (expedited: boolean): React.ReactElement =>
    expedited ? <span>Yes</span> : <span>No</span>;

  const columns: IColumn[] = [
    {
      key: "id",
      name: "ID",
      ariaLabel: "Azure request ID",
      fieldName: "id",
      minWidth: 160,
      isResizable: true,
      onRender: (item, _index, _column): React.ReactElement => idLink(item.id),
    },
    {
      key: "firstName",
      name: "First Name",
      ariaLabel: "First name",
      fieldName: "firstName",
      minWidth: 160,
      isResizable: true,
    },
    {
      key: "lastName",
      name: "Last Name",
      ariaLabel: "Last name",
      fieldName: "lastName",
      minWidth: 160,
      isResizable: true,
    },
    {
      key: "requestedDate",
      name: "Date Requested",
      ariaLabel: "Date Requested",
      fieldName: "requestedDate",
      minWidth: 160,
      isResizable: true,
      onRender: (item) => formatDate(item.requestedDate),
    },
    {
      key: "expedited",
      name: "Expedited",
      ariaLabel: "Expedited",
      fieldName: "expedited",
      minWidth: 160,
      isResizable: true,
      onRender: (item, _index, _column): React.ReactElement => formatExpeditedField(item.expedited),
    },
  ];

  if (isLoadingAzureRequests) {
    return <Spinner size={SpinnerSize.large} styles={spinnerStyles} />;
  } else if (loadAzureRequestsError !== "") {
    return <div>{loadAzureRequestsError}</div>;
  }

  const onClickAzureBenefitRequest = (userProfileId: number): void => {
    dispatch(getAzureBenefitRequestDetails(userProfileId, tenant));
    history.push(`/${tenant}/benefits/azureRequests/details/${userProfileId}`);
  };

  const onRenderRow = (props?: IDetailsRowProps, defaultRender?: any): React.ReactElement => {
    const userProfileId = props?.item.userProfileId;
    return defaultRender({
      ...props,
      onClick: () => onClickAzureBenefitRequest(userProfileId),
    });
  };

  return (
    <>
      <DetailsList
        items={data}
        columns={columns}
        selectionMode={SelectionMode.none}
        getKey={(item: IAzureBenefitRequest): string => `${item.firstName}${item.requestedDate}`}
        setKey="none"
        layoutMode={DetailsListLayoutMode.fixedColumns}
        isHeaderVisible={true}
        enterModalSelectionOnTouch={true}
        onRenderRow={onRenderRow}
        styles={detailListStyles}
      />
      <PaginationRow>
        <Pagination
          total={filteredCount}
          onChange={(page) => dispatch(VisualStudioBenefitActions.updateCurrentPage(page))}
          showTitle={false}
          current={currentPage}
          prevIcon={ChevronIcon("ChevronLeft")}
          nextIcon={ChevronIcon("ChevronRight")}
          pageSize={pageSize}
          showSizeChanger
        />
        <Dropdown
          options={[
            { key: 16, text: "Show 16 results" },
            { key: 32, text: "Show 32 results" },
            { key: 48, text: "Show 48 results" },
          ]}
          onChange={(_e, op): void => {
            dispatch(VisualStudioBenefitActions.updatePageSize(op?.key as number));
          }}
          selectedKey={pageSize}
          className="pagination-dropdown"
          title="Page Size Dropdown"
        />
      </PaginationRow>
    </>
  );
};

export default AzureRequestsList;
